// ExtraVideos — manage a gallery of extra Mux clips for a catalog video.
// Each entry: { id, label, mux_playback_id, mux_status, mux_duration }.
// Two add modes: upload new (Mux direct upload) or link existing playback_id.
import React, { useState, useEffect } from "react";
import { Trash2, Play, RefreshCw, Upload as UploadIcon, Link as LinkIcon, Edit3, X, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import MuxUploader from "@mux/mux-uploader-react";
import { api } from "../lib/api";

export default function ExtraVideos({ videoId, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [addMode, setAddMode] = useState(null); // null | "upload" | "link" | "library"
  const [linkData, setLinkData] = useState({ playback_id: "", label: "" });
  const [editing, setEditing] = useState(null);

  const [uploadInfo, setUploadInfo] = useState(null); // {upload_url}
  const [pendingLabel, setPendingLabel] = useState("");

  // Universal Mux Library — pulls from Mux account directly (all uploads ever)
  const [library, setLibrary] = useState([]);
  const [libraryQuery, setLibraryQuery] = useState("");
  const [loadingLib, setLoadingLib] = useState(false);

  useEffect(() => {
    if (addMode === "library" && library.length === 0) {
      setLoadingLib(true);
      api.get("/api/mux/assets?limit=100")
        .then((r) => setLibrary(r.data?.assets || []))
        .catch(() => toast.error("Could not load Mux library"))
        .finally(() => setLoadingLib(false));
    }
  }, [addMode]); // eslint-disable-line

  if (!videoId) {
    return (
      <div className="rounded-lg border p-4 text-[12px] italic" data-testid="vadd-extras-disabled"
        style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)", color: "var(--gio-muted-3)" }}>
        💡 Save the video first to add extra clips.
      </div>
    );
  }

  const refresh = async () => {
    try {
      const r = await api.get(`/api/videos/${videoId}`);
      onChange(r.data?.extra_videos || []);
    } catch (e) { /* silent */ }
  };

  const startUpload = async () => {
    setBusy(true);
    try {
      const r = await api.post("/api/mux/extra/upload", { video_id: videoId, label: pendingLabel });
      setUploadInfo({ upload_url: r.data.upload_url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to start upload");
    } finally { setBusy(false); }
  };

  const handleLink = async () => {
    if (!linkData.playback_id.trim()) { toast.error("Playback ID required"); return; }
    setBusy(true);
    try {
      await api.post("/api/mux/extra/link", {
        video_id: videoId,
        playback_id: linkData.playback_id.trim(),
        label: linkData.label.trim() || null,
      });
      toast.success("Extra clip linked");
      setLinkData({ playback_id: "", label: "" });
      setAddMode(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (eid) => {
    if (!window.confirm("Remove this extra clip? The Mux asset will be deleted.")) return;
    try {
      await api.delete(`/api/mux/extra/${videoId}/${eid}`);
      toast.success("Removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const saveRename = async (eid, label) => {
    try {
      await api.patch(`/api/mux/extra/${videoId}/${eid}`, { label });
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const pickFromLibrary = async (pid) => {
    setBusy(true);
    try {
      await api.post("/api/mux/extra/link", {
        video_id: videoId,
        playback_id: pid,
        label: pendingLabel.trim() || null,
      });
      toast.success("Linked from Mux library");
      setPendingLabel("");
      setAddMode(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    } finally {
      setBusy(false);
    }
  };

  const list = value || [];

  return (
    <div className="rounded-lg border p-4 space-y-3" data-testid="vadd-extra-videos"
      style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
      <div className="overline flex items-center justify-between">
        <span>🎬 EXTRA VIDEOS · {list.length}</span>
        <button type="button" onClick={refresh}
          className="text-[11px] text-zinc-500 hover:text-white inline-flex items-center gap-1"
          data-testid="vadd-extras-refresh"
          title="Refresh statuses">
          <RefreshCw size={11} />
        </button>
      </div>

      {/* Existing extras list */}
      {list.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {list.map((ex) => (
            <div key={ex.id} className="relative group rounded overflow-hidden border"
              style={{ borderColor: "var(--gio-border)", background: "black" }}
              data-testid={`vadd-extra-${ex.id}`}>
              {ex.mux_playback_id ? (
                <img src={`https://image.mux.com/${ex.mux_playback_id}/thumbnail.jpg?width=240&height=135&fit_mode=smartcrop&time=1`}
                  alt={ex.label} className="w-full h-24 object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-24 flex items-center justify-center text-[10px] text-zinc-500">
                  <RefreshCw size={14} className="animate-spin mr-1" /> {ex.mux_status || "processing"}…
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent flex flex-col justify-end p-2">
                {editing === ex.id ? (
                  <input
                    autoFocus defaultValue={ex.label}
                    data-testid={`vadd-extra-rename-${ex.id}`}
                    onBlur={(e) => saveRename(ex.id, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditing(null); }}
                    className="text-[11px] bg-black/80 border border-zinc-700 text-white px-1 py-0.5 rounded outline-none"
                  />
                ) : (
                  <button type="button" onClick={() => setEditing(ex.id)} data-testid={`vadd-extra-label-${ex.id}`}
                    className="text-[11px] text-white text-left truncate inline-flex items-center gap-1 hover:text-[color:var(--gio-secondary)]"
                    title="Click to rename">
                    {ex.label} <Edit3 size={8} className="opacity-60" />
                  </button>
                )}
                {ex.mux_duration && (
                  <span className="text-[9px] text-zinc-400 font-mono-ui">{Math.round(ex.mux_duration)}s</span>
                )}
              </div>
              {ex.mux_playback_id && (
                <a href={`https://stream.mux.com/${ex.mux_playback_id}.m3u8`} target="_blank" rel="noreferrer"
                  data-testid={`vadd-extra-play-${ex.id}`}
                  className="absolute top-1 left-1 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <Play size={11} className="text-[color:var(--gio-secondary)]" />
                </a>
              )}
              <button type="button" onClick={() => removeOne(ex.id)} data-testid={`vadd-extra-remove-${ex.id}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"
                style={{ background: "rgba(220,38,38,0.85)", color: "#fff" }}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add UI */}
      {!addMode && (
        <div className="grid grid-cols-3 gap-2">
          <button type="button" onClick={() => setAddMode("upload")} data-testid="vadd-extra-upload-mode"
            className="py-2 px-3 rounded border border-dashed text-[12px] font-mono-ui uppercase hover:bg-[color:var(--gio-primary-soft)] inline-flex items-center justify-center gap-2"
            style={{ borderColor: "var(--gio-primary)", color: "var(--gio-primary)" }}>
            <UploadIcon size={12} /> Upload
          </button>
          <button type="button" onClick={() => setAddMode("library")} data-testid="vadd-extra-library-mode"
            className="py-2 px-3 rounded border border-dashed text-[12px] font-mono-ui uppercase hover:bg-[color:#a855f7]/10 inline-flex items-center justify-center gap-2"
            style={{ borderColor: "#a855f7", color: "#a855f7" }}>
            <FolderOpen size={12} /> Library
          </button>
          <button type="button" onClick={() => setAddMode("link")} data-testid="vadd-extra-link-mode"
            className="py-2 px-3 rounded border border-dashed text-[12px] font-mono-ui uppercase hover:bg-[color:var(--gio-secondary)]/10 inline-flex items-center justify-center gap-2"
            style={{ borderColor: "var(--gio-secondary)", color: "var(--gio-secondary)" }}>
            <LinkIcon size={12} /> Mux ID
          </button>
        </div>
      )}

      {addMode === "library" && (
        <div className="p-3 rounded border space-y-2" data-testid="vadd-extra-library-panel"
          style={{ borderColor: "#a855f7", background: "var(--gio-surface-2)" }}>
          <div className="flex items-center justify-between">
            <div className="overline" style={{ color: "#a855f7" }}>
              📁 PICK FROM MUX LIBRARY · {library.length}
            </div>
            <button type="button" onClick={() => { setAddMode(null); setPendingLabel(""); }}
              className="text-zinc-500 hover:text-white"><X size={14} /></button>
          </div>
          <input data-testid="vadd-extra-library-label" placeholder="Label (optional, e.g. 'Behind the scenes')"
            value={pendingLabel} onChange={(e) => setPendingLabel(e.target.value)} disabled={busy}
            className="gio-input w-full text-[12px]" />
          <input value={libraryQuery} onChange={(e) => setLibraryQuery(e.target.value)}
            placeholder="Filter by asset ID or playback ID…"
            data-testid="vadd-extra-library-search"
            className="gio-input w-full text-[12px] font-mono-ui" />
          {loadingLib && (
            <div className="text-center py-6 text-[12px]" style={{ color: "var(--gio-muted-3)" }}>
              <RefreshCw size={14} className="animate-spin inline mr-1" /> Loading Mux assets…
            </div>
          )}
          {!loadingLib && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {library
                .filter(a => !libraryQuery ||
                  (a.id || "").toLowerCase().includes(libraryQuery.toLowerCase()) ||
                  (a.playback_id || "").toLowerCase().includes(libraryQuery.toLowerCase()))
                .map((a) => (
                  <button type="button" key={a.id}
                    onClick={() => a.playback_id && pickFromLibrary(a.playback_id)}
                    disabled={busy || !a.playback_id}
                    data-testid={`vadd-extra-library-pick-${a.id}`}
                    className="group relative rounded overflow-hidden border hover:border-[color:#a855f7] disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                    {a.playback_id ? (
                      <img src={`https://image.mux.com/${a.playback_id}/thumbnail.jpg?width=240&height=135&fit_mode=smartcrop&time=2`}
                        alt="" className="w-full aspect-video object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full aspect-video flex items-center justify-center text-[10px] text-zinc-500">
                        no public playback
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-1 bg-black/85 text-[9px] text-white text-left">
                      <div className="font-mono-ui truncate">{a.id}</div>
                      <div className="text-[8px] opacity-70">
                        {a.status}{a.duration ? ` · ${Math.round(a.duration)}s` : ""}
                      </div>
                    </div>
                  </button>
                ))}
              {library.length === 0 && (
                <div className="col-span-full text-[12px] italic text-center py-6" style={{ color: "var(--gio-muted-3)" }}>
                  No Mux assets found in your account.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {addMode === "upload" && (
        <div className="p-3 rounded border space-y-2" style={{ borderColor: "var(--gio-primary)", background: "var(--gio-surface-2)" }}>
          <div className="flex items-center justify-between">
            <div className="overline" style={{ color: "var(--gio-primary)" }}>UPLOAD NEW CLIP</div>
            <button type="button" disabled={busy}
              onClick={() => { setAddMode(null); setUploadInfo(null); setPendingLabel(""); }}
              className="text-zinc-500 hover:text-white"><X size={14} /></button>
          </div>
          {!uploadInfo ? (
            <>
              <input data-testid="vadd-extra-upload-label" placeholder="Label (optional, e.g. 'Alternate cut')"
                value={pendingLabel} onChange={(e) => setPendingLabel(e.target.value)} disabled={busy}
                className="gio-input w-full text-[12px]" />
              <button type="button" onClick={startUpload} disabled={busy} data-testid="vadd-extra-upload-start"
                className="btn-primary w-full text-[12px] inline-flex items-center justify-center gap-2">
                {busy ? <RefreshCw size={12} className="animate-spin" /> : <UploadIcon size={12} />}
                {busy ? "Preparing…" : "Choose video file"}
              </button>
            </>
          ) : (
            <div data-testid="vadd-extra-uploader-wrap">
              <MuxUploader
                endpoint={uploadInfo.upload_url}
                chunkSize={5120}
                dynamicChunkSize
                onSuccess={async () => {
                  toast.success("Extra clip uploaded — Mux is processing");
                  setUploadInfo(null);
                  setPendingLabel("");
                  setAddMode(null);
                  await refresh();
                }}
                onUploadError={(e) => {
                  toast.error("Upload error: " + (e?.detail?.message || "unknown"));
                }}
                style={{ "--button-background-color": "var(--gio-primary)", "--button-color": "#000" }}
              />
            </div>
          )}
        </div>
      )}

      {addMode === "link" && (
        <div className="p-3 rounded border space-y-2" style={{ borderColor: "var(--gio-secondary)", background: "var(--gio-surface-2)" }}>
          <div className="flex items-center justify-between">
            <div className="overline" style={{ color: "var(--gio-secondary)" }}>LINK EXISTING MUX ASSET</div>
            <button type="button" onClick={() => setAddMode(null)} className="text-zinc-500 hover:text-white"><X size={14} /></button>
          </div>
          <input data-testid="vadd-extra-link-pid" placeholder="Mux playback ID (e.g. xyz123abc)"
            value={linkData.playback_id} onChange={(e) => setLinkData({ ...linkData, playback_id: e.target.value })}
            className="gio-input w-full text-[12px]" />
          <input data-testid="vadd-extra-link-label" placeholder="Label (optional, e.g. 'Behind the scenes')"
            value={linkData.label} onChange={(e) => setLinkData({ ...linkData, label: e.target.value })}
            className="gio-input w-full text-[12px]" />
          <button type="button" onClick={handleLink} disabled={busy} data-testid="vadd-extra-link-save"
            className="btn-primary w-full text-[12px]">
            {busy ? "Linking…" : "Link clip"}
          </button>
        </div>
      )}
    </div>
  );
}
